import type { ApiFactory, ApiRef } from '@backstage/frontend-plugin-api';
import type { AnyRouter } from '@trpc/server';

import {
  ApiBlueprint,
  configApiRef,
  createExtensionBlueprint,
  createExtensionBlueprintParams,
  fetchApiRef,
  identityApiRef,
} from '@backstage/frontend-plugin-api';
import {
  createTRPCClient,
  httpBatchLink,
  httpSubscriptionLink,
  retryLink,
  splitLink,
} from '@trpc/client';
import {
  createTRPCOptionsProxy,
  type TRPCOptionsProxy,
} from '@trpc/tanstack-react-query';
import { EventSourcePolyfill } from 'event-source-polyfill';

import { queryClient } from '../queryClient';

const apiFactory = <
  TApi,
  TImpl extends TApi,
  TDeps extends Record<string, unknown>,
>(
  api: ApiFactory<TApi, TImpl, TDeps>,
) => api;

export const TrpcApiBlueprint = createExtensionBlueprint({
  attachTo: { id: 'root', input: 'apis' },
  defineParams<TApi extends AnyRouter>(params: {
    apiRef: ApiRef<TRPCOptionsProxy<TApi>>;
    path: string;
  }) {
    return createExtensionBlueprintParams(params);
  },
  * factory(params) {
    yield ApiBlueprint.dataRefs.factory(
      apiFactory({
        api: params.apiRef,
        deps: {
          config: configApiRef,
          fetchApi: fetchApiRef,
          identityApi: identityApiRef,
        },
        factory: ({ config, fetchApi, identityApi }) => {
          const baseUrl = config.getString('backend.baseUrl');
          const url = `${baseUrl}/api/${params.path}`;

          const subscriptionRetry = retryLink({
            retry: opts => {
              const code = opts.error.data?.code;
              if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN') {
                console.warn('Retrying due to 401/403 error');
                return true;
              }
              return false;
            },
          });
          const subscriptionLink = httpSubscriptionLink({
            EventSource: EventSourcePolyfill,
            eventSourceOptions: async () => {
              const { token } = await identityApi.getCredentials();
              return {
                // sending this header only works b/c of the event-source-polyfill
                headers: {
                  authorization: `Bearer ${token}`,
                },
              };
            },
            url,
          });
          const batchLink = httpBatchLink({ fetch: fetchApi.fetch, url });

          return createTRPCOptionsProxy({
            client: createTRPCClient({
              links: [
                splitLink({
                  condition: op => op.type === 'subscription',
                  false: batchLink,
                  true: [subscriptionRetry, subscriptionLink],
                }),
              ],
            }),
            queryClient,
          });
        },
      }),
    );
  },
  kind: 'api',
  output: [ApiBlueprint.dataRefs.factory],
});
